import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetNotificationsByWorkspace } from '@/api/messages/useGetNotificationsByWorkspace';
import { Button } from '@/component-library/primitives/Button/Button';
import BellRingIcon from '@/component-library/primitives/Icons/BellRingIcon/BellRingIcon';
import ChatIcon from '@/component-library/primitives/Icons/ChatIcon/ChatIcon';
import ChevronIcon from '@/component-library/primitives/Icons/ChevronIcon/ChevronIcon';
import EmailIcon from '@/component-library/primitives/Icons/EmailIcon/EmailIcon';
import Loader from '@/component-library/primitives/Loader/Loader';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import SearchField from '@/component-library/widgets/SearchField/SearchField';
import SelectCustom, { SelectCustomOption } from '@/component-library/widgets/SelectCustom/SelectCustom';
import Table from '@/component-library/widgets/Table/Table';
import { useSingleSendsEnabled } from '@/hooks/useSingleSendsEnabled';
import { usePageTracking } from '@/hooks/useTracking';
import useOutsideClick from '@/modules/shared/hooks/useOutsideClick';
import { AppRoutes } from '@/utils/routes/router';

import { useDeliveryTable } from './useDeliveryTable';

const Delivery = () => {
  const { columns, selectOptions } = useDeliveryTable();
  const { data, isLoading } = useGetNotificationsByWorkspace();
  const [filter, setFilter] = useState<SelectCustomOption | null>(selectOptions[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const singleSendsEnabled = useSingleSendsEnabled();

  const filteredData = data?.filter((notif) => {
    if (filter?.value === '') {
      return data;
    } else {
      return notif.channel === filter?.value.toUpperCase();
    }
  });

  usePageTracking('delivery', { component: 'delivery' });

  const [globalFilterValue, setGlobalFilterValue] = useState('');

  const globalFilteredData = useMemo(() => {
    return filteredData?.filter((row) =>
      Object.values(row).some(
        (value) => value !== null && value.toString().toLowerCase().includes(globalFilterValue.toLowerCase()),
      ),
    );
  }, [filteredData, globalFilterValue]);

  const menuOptions = singleSendsEnabled
    ? [
        {
          icon: <ChatIcon />,
          name: t('DeliveryPage.buttonSms'),
          route: AppRoutes.newSMS,
        },
        { icon: <EmailIcon />, name: t('DeliveryPage.buttonEmail'), route: AppRoutes.newEmail },
        {
          icon: <BellRingIcon color="#1C222A" width="24" height="24" />,
          name: t('DeliveryPage.buttonPushNotification'),
          route: AppRoutes.newPush,
        },
      ]
    : [];

  const newMessageDropdownPanelClass =
    'absolute z-10 mt-2 p-2 bg-white rounded-lg border border-custom-lightSilver shadow-md whitespace-nowrap';
  const newMessageDropdownItemClass =
    'group flex flex-row items-center gap-2 px-2 py-3 cursor-pointer rounded-md hover:bg-custom-silver/50 text-custom-black';

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useOutsideClick(dropdownRef, () => setIsDropdownOpen(false));

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-custom-black">{t('DeliveryPage.title')}</h1>
        <p className="mt-1 text-sm text-custom-darkSilver">{t('DeliveryPage.subtitle')}</p>
      </div>
      <div className="flex justify-between items-center gap-4 mb-4 flex-wrap">
        <SearchField initialValue={globalFilterValue} setSearchValue={setGlobalFilterValue} />
        {singleSendsEnabled && globalFilteredData && globalFilteredData.length > 0 && (
          <div className="relative ml-auto">
            <Button onClick={toggleDropdown} ref={dropdownRef} className="group !w-[10.5rem]">
              <span className="flex flex-row gap-3 justify-center items-center">
                <span>{t('DeliveryPage.buttonNewMessage')}</span>
                <span className="group-hover:[&>*_path]:fill-gray-900 group-active:[&>*_path]:fill-white">
                  <ChevronIcon color="#1C222A" type={isDropdownOpen ? 'up' : 'down'} />
                </span>
              </span>
            </Button>
            {isDropdownOpen && (
              <div className={`${newMessageDropdownPanelClass} right-0 top-12 w-fit`}>
                {menuOptions.map((option, index) => (
                  <div key={index} className={newMessageDropdownItemClass} onClick={() => navigate(option.route)}>
                    <span className="[&_path]:stroke-custom-black">{option.icon}</span>
                    <span className="font-light text-sm tracking-[0.0175rem]">{option.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {isLoading ? (
        <Loader />
      ) : globalFilteredData && globalFilteredData.length > 0 ? (
        <>
          <SelectCustom options={selectOptions} filter={filter} setFilter={setFilter} />
          <Table columns={columns} data={globalFilteredData ?? []} />
        </>
      ) : (
        <>
          <SelectCustom options={selectOptions} filter={filter} setFilter={setFilter} />
          <NoDataBoard
            description={t('NoDataBoard.sendYourFirstMessage')}
            actionButton={
              <div className="relative justify-end mt-4">
                <Button onClick={toggleDropdown} ref={dropdownRef} className="group !w-[10.5rem]">
                  <span className="flex flex-row gap-3 justify-center items-center">
                    <span>{t('DeliveryPage.buttonNewMessage')}</span>
                    <span className="group-hover:[&>*_path]:fill-gray-900 group-active:[&>*_path]:fill-white">
                      <ChevronIcon color="#1C222A" type={isDropdownOpen ? 'up' : 'down'} />
                    </span>
                  </span>
                </Button>
                {isDropdownOpen && (
                  <div className={`${newMessageDropdownPanelClass} left-0 top-12 w-fit`}>
                    {menuOptions.map((option, index) => (
                      <div key={index} className={newMessageDropdownItemClass} onClick={() => navigate(option.route)}>
                        <span className="[&_path]:stroke-custom-black">{option.icon}</span>
                        <span className="font-light text-sm tracking-[0.0175rem]">{option.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            }
          />
        </>
      )}
    </div>
  );
};

export default Delivery;
